/**
 * useBenefits Hook
 *
 * React Query hooks for investment benefit operations
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';

import {
  benefitsService,
  CreateBenefitDto,
  UpdateBenefitDto,
  BenefitSummary,
} from '../services/benefits.service';
import { unwrapResponse } from '../types';

import { investmentKeys } from './useInvestments';

import type { InvestmentBenefit } from '@medsuite/models';

export const benefitKeys = {
  all: ['benefits'] as const,
  lists: () => [...benefitKeys.all, 'list'] as const,
  list: (investmentId: number) => [...benefitKeys.lists(), investmentId] as const,
  summary: (investmentId: number) => [...benefitKeys.all, 'summary', investmentId] as const,
};

/**
 * Fetch all benefits for an investment
 */
export function useBenefits(
  investmentId: number,
  options?: Omit<UseQueryOptions<InvestmentBenefit[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<InvestmentBenefit[], Error>({
    queryKey: benefitKeys.list(investmentId),
    queryFn: async () => {
      const response = await benefitsService.getAll(investmentId);
      // Extract data from success wrapper: {success: true, data: {investment_id, benefits[], total}, meta: {}}
      const responseData = unwrapResponse<{ benefits?: InvestmentBenefit[] }>(response.data);
      // The API returns {investment_id, benefits, total}, extract the benefits array
      const benefits = responseData.benefits || [];

      // Convert string values to numbers (database returns DECIMAL as strings)
      return benefits.map((benefit: InvestmentBenefit) => ({
        ...benefit,
        amount: benefit.amount ? parseFloat(String(benefit.amount)) : null,
      }));
    },
    ...options,
  });
}

/**
 * Fetch benefit summary
 */
export function useBenefitSummary(
  investmentId: number,
  options?: Omit<UseQueryOptions<BenefitSummary, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<BenefitSummary, Error>({
    queryKey: benefitKeys.summary(investmentId),
    queryFn: async () => {
      const response = await benefitsService.getSummary(investmentId);
      // Extract data from success wrapper: {success: true, data: {summary}, meta: {}}
      const responseData = unwrapResponse<{ summary?: Record<string, unknown> }>(response.data);
      // The API returns {summary}, extract it
      const summary = (responseData.summary || responseData) as Record<string, unknown>;

      // Map API field names to expected names and parse string values to numbers
      return {
        ...summary,
        type1_count: (summary.quantitative_count as number) || 0,
        type2_count: (summary.non_quantitative_count as number) || 0,
        total_monetary_benefit: parseFloat(String(summary.total_amount)) || 0,
      } as BenefitSummary;
    },
    ...options,
  });
}

/**
 * Create new benefit
 */
export function useCreateBenefit(
  investmentId: number,
  options?: UseMutationOptions<InvestmentBenefit, Error, Omit<CreateBenefitDto, 'investment_id'>>
) {
  const queryClient = useQueryClient();

  return useMutation<InvestmentBenefit, Error, Omit<CreateBenefitDto, 'investment_id'>>({
    mutationFn: async (data) => {
      const response = await benefitsService.create(investmentId, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: benefitKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: benefitKeys.summary(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
    },
    ...options,
  });
}

/**
 * Update benefit
 */
export function useUpdateBenefit(
  investmentId: number,
  options?: UseMutationOptions<
    InvestmentBenefit,
    Error,
    { benefitId: number; data: UpdateBenefitDto }
  >
) {
  const queryClient = useQueryClient();

  return useMutation<InvestmentBenefit, Error, { benefitId: number; data: UpdateBenefitDto }>({
    mutationFn: async ({ benefitId, data }) => {
      const response = await benefitsService.update(investmentId, benefitId, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: benefitKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: benefitKeys.summary(investmentId) });
    },
    ...options,
  });
}

/**
 * Delete benefit
 */
export function useDeleteBenefit(
  investmentId: number,
  options?: UseMutationOptions<void, Error, number>
) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (benefitId) => {
      await benefitsService.delete(investmentId, benefitId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: benefitKeys.list(investmentId) });
      queryClient.invalidateQueries({ queryKey: benefitKeys.summary(investmentId) });
      queryClient.invalidateQueries({ queryKey: investmentKeys.summary(investmentId) });
    },
    ...options,
  });
}
